import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { ShareBoxIcon, Loading, QuestionIcon, Selector } from '../../../components';
import {Delete} from '../../../components/Icons'
import { isNullOrEmptyArray } from '../../../util/arrays';
import { fetchSurveyById, shareSurvey } from '../../../stores/actions/management';
import { usePromptConfirm, useErrorToast, usePromptAlert } from '../../../hooks';
import * as Ui from './styles';

// {id: 3646, descricao: "aeroporto recife", compartilhamentos: Array(1)}
// {id: 21, cliente: 2, ativo: true, is_principal: true}
function ShareModal({
  // {id, titulo}
  /**
   * identidade do cliente atual
   */
  customerId,
  /**
   * identidade da pesquisa a ser compartilhada
   */
  surveyId,
  /**
   * propaga o evento de salvar
   */
  onSave,
  /**
   * propaga o evento de fechamento do modal
   */
  onClose,
  /**
   * outros
   */
  ...props
}, ref) {
  // from redux
  const { dispatch, running, error, surveyData, customersData } = props;
  // states
  const [waitingToSave, setWaitingToSave] = useState(false);
  const [surveyTitle, setSurveyTitle] = useState(null);
  const [shares, setShares] = useState([]);
  const [keepConfig, setKeepConfig] = useState(false);
  const [customers, setCustomers] = useState([]);
  // hooks
  const [confirmSave, renderSaveConfirmation] = usePromptConfirm();
  const [confirmRemove, renderRemovingConfirmation] = usePromptConfirm();
  const [checkError, confirmSuccess] = useErrorToast();
  const [openNotAllowsAlert, renderNotAllowsAlert] = usePromptAlert();

  // get customer name like callback
  const getCustomerName = useCallback((cId) => {
    let customerName = 'Não definido';
    customersData.forEach((customer) => {
      if (customer.id === cId) {
        customerName = customer.nome;
      }
    });
    return customerName;
  }, [customersData]);

  // após terminar de salvar os dados
  useEffect(() => {
    if (waitingToSave) {
      setWaitingToSave(false);
      confirmSuccess();
      if (onSave) onSave();
    }
  }, [waitingToSave, onSave, confirmSuccess]);

  // error effect
  useEffect(() => {
    if (error) {
      checkError(error);
    }
  }, [checkError, error]);

  /**
   * Callbacks: Transforma um objeto de cliente advindo de "surveyData.compartilhamentos"
   * para o modelo esperado pelo estado "shares".
   */
  const parseClientToSharesObject = useCallback((c) => ({
    value: { customerId: c.cliente, isPrincipal: c.is_principal },
    label: getCustomerName(c.cliente),
  }), []);

  // result of fetch survey data (resp)
  useEffect(() => {
    if (surveyData) {
      setSurveyTitle(surveyData.descricao);
      if (!isNullOrEmptyArray(surveyData.compartilhamentos)) {
        setKeepConfig(!!surveyData.manter_configuracoes);

        // criamos uma referência para uma variável que irá armazenar o cliente principal
        let principalClient;

        let sharesToObject = surveyData.compartilhamentos.map((c) => {
          // se for o cliente principal, armazenamos a referência dele em "principalClient"
          if (c.is_principal) {
            principalClient = c;
            // retornarmos null para podermos realocar o array mapeado de clientes
            return null;
          };

          return parseClientToSharesObject(c);
        });
        
        if (principalClient) {
          // Se o armazenamento do cliente principal foi feito com sucesso, nós reordenamos o array
          // para o cliente principal armazenado ser o primeiro a aparecer na listagem.
          sharesToObject = [
            parseClientToSharesObject(principalClient), 
            ...sharesToObject.filter(client => client)
          ];
        }

        setShares(sharesToObject);
      }
    }
  }, [getCustomerName, surveyData]);

  // initial effect (req)
  useEffect(() => {
    if (surveyId) {
      fetchSurveyById(customerId, surveyId, dispatch);
    }
  }, [customerId, surveyId, dispatch]);

  //
  useEffect(() => {
    if (!isNullOrEmptyArray(customersData)) {
      const sharesIds = shares.map((s) => s.value.customerId);
      const filteredCustomersData = customersData.filter((c) => !sharesIds.includes(c.id));
      setCustomers(filteredCustomersData.map((c) => ({
        value: c.id, label: c.nome,
      })));
    }
  }, [customersData, shares]);

  // manipula o acionamento do botão "salvar"
  function handleOk() {
    confirmSave(() => {
      setWaitingToSave(true);
      const cids = shares.map((s) => s.value.customerId);
      shareSurvey(customerId, surveyId, cids, keepConfig, dispatch);
    });
  }

  // manipula o acionamento do botão "cancelar"
  function handleClose() {
    if (onClose) onClose();
  }

  // manipula o acionamento de checagem para manter dados
  function handleCheckKeepConfig() {
    setKeepConfig(!keepConfig);
  }

  function handleSelectCustomer(sel) {
    // recupera o cliente selecionado
    const customer = sel[0];
    const cid = customer.value;
    // captura os dados da lista principal de clientes
    const newShares = [...shares];
    newShares.push({
      value: { customerId: cid, isPrincipal: false },
      label: getCustomerName(cid),
    });
    setShares(newShares);
  }

  return (
    <Ui.StyledModal
      isOpen
      title={'Compartilhar Pesquisa'}
      okBtnTitle={'Salvar'}
      cancelBtnTitle={'Cancelar'}
      onCancel={() => handleClose()}
      onOk={() => handleOk()}
      iconComponent={(<ShareBoxIcon />)}
    >
      {(running) ? (
        <Loading size={30} running={running} />
      ) : (
        <Ui.Container>
          <Ui.ContainerBody>
            <Ui.Label>{surveyTitle}</Ui.Label>
            <Selector
              type="single"
              placeholder="Selecione usuário para compartilhar"
              inputSearchPlaceholder="Busque o nome de um usuário"
              data={customers}
              isLoading={running}
              onChange={handleSelectCustomer}
              enableFilter
            />
            <Ui.UsersList>
              {shares.map((s) => (
                <Ui.UsersListItem>
                  <Ui.UsersListItemName>
                    {s.label}
                    {s.value.isPrincipal && ' (principal)'}
                  </Ui.UsersListItemName>
                </Ui.UsersListItem>
              ))}
            </Ui.UsersList>
          </Ui.ContainerBody>
          <Ui.ContainerFooter>
            <Ui.StyledCheckButton
              label={'Manter configurações da pesquisa'}
              checked={keepConfig}
              onChange={() => handleCheckKeepConfig()}
            />
          </Ui.ContainerFooter>
        </Ui.Container>
      )}
      {renderRemovingConfirmation(Delete, 'Deseja realmente remover este cliente?')}
      {renderSaveConfirmation(QuestionIcon, 'Deseja realizar as alterações nesta pesquisa')}
      {renderNotAllowsAlert(QuestionIcon, 'Não é possível remover o compartilhamento com o cliente principal')}
    </Ui.StyledModal>
  );
}

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * surveys => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state) => {
  // get state
  const surveyState = state.management.survey;
  const customersState = state.tenants;
  // result
  return {
    running: surveyState.running,
    error: surveyState.error,
    surveyData: surveyState.payload,
    customersData: customersState.payload,
    completed: !!surveyState.completed,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: false },
)(ShareModal);
